import { useContext, useEffect, useState } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { Formik, Form } from 'formik';
import {
  LIST_COUNTRIES,
  useConsumerHoldingEditor,
  useGetConsumerHoldings,
} from '@rabbit/bizproc/react';
import {
  Button,
  DesktopContainer,
  Input,
  LoadingSpinner,
} from '@rabbit/elements/shared-components';
import { isSerialNumberValid, isSerialNumberValidV2 } from '@rabbit/elements/shared-components';
import ROUTE_NAME from 'apps/olive/src/utils/url-constants';
import { AppContext, getEditableFields } from '@rabbit/app-context';
import { t } from 'i18next';
import { EditableTypes } from '@rabbit/helper';
import { ConsumerHoldingSummaryShape } from '@rabbit/bizproc/client';
import { FBD_Vendable } from '@rabbit/data/types';
import CommonFormBuilder from 'libs/elements/shared-components/src/lib/molecules/CommonFormBuilder/CommonFormBuilder';
import { useMyConsumerPersona } from '@rabbit/data/portal';
import { nestApiGetMakesModels } from '@rabbit/bizproc/core';

/* eslint-disable-next-line */
export interface HoldingSingleEditViewProps { }

interface FormValuesShape {
  serial: string;
  purchase_location: {
    country: string;
    docid: string;
  } | null;
  purchase_time: number;
  store_not_listed: boolean;
  custom_store_name: string;
  purchase_country: string;
  additionalData?: {
    [key: string]: string | number | boolean | null | undefined;
  };
}

export function HoldingSingleEditView(props: HoldingSingleEditViewProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { config } = useContext(AppContext);
  const consumerPersona = useMyConsumerPersona();
  const consumerPersonaId = consumerPersona?.personaId;
  const { getSingleHolding } = useGetConsumerHoldings(consumerPersonaId || '');
  const holdingId = location.pathname.split('/')[2];
  const { body, isReady, update, commit } = useConsumerHoldingEditor(
    consumerPersonaId ?? '',
    holdingId
  );
  const [readyToEdit, setReadyToEdit] = useState(isReady);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setLoading] = useState(true);
  const [storeLabel, setStoreLabel] = useState<string>(
    t('Enter store name & location')
  );
  const [fields, setFields] = useState<any>(
    config.HOLDINGS.NEW_REGISTRATION_FLOW.ADDITIONAL_FIELDS
  );

  const {
    public: holding_public,
    manufacturer: holding_manufacturer,
    private: holding_private,
  } = body;

  // Decide if serial can be edited:
  // - global serial validation must be enabled
  // - serialNumberVerified is false in of holding collection
  const serialVerified = holding_public?.serialNumberVerified === true;
  const canEditSerial =
    Boolean(config.HOLDINGS.SERIAL_NUMBER_VALIDATION.ENABLED) && Boolean(!serialVerified);
  const productDocumentCount =
    (holding_private?.receipt?.length ?? 0) +
    (holding_private?.serial_proof?.length ?? 0);

  // Load vendable serial validation data
  const [serialNumbers, setSerialNumbers] = useState<string[]>([]);
  const [serialNumberRanges, setSerialNumberRanges] = useState<
    Array<{ start: string; end: string }>
  >([]);
  useEffect(() => {
    if (config.HOLDINGS.SERIAL_NUMBER_VALIDATION.ENABLED) {
      const loadVendable = async () => {
        try {
          const vendableId = holding_public?.vendable;
          if (!vendableId) return;
          const vendable = await FBD_Vendable.get(vendableId);
          if (vendable) {
            setSerialNumbers(vendable.serialNumbers || []);
            setSerialNumberRanges(vendable.serialNumberRanges || []);
          }
        } catch {
          // ignore errors; treat as not verifiable
        }
      };
      loadVendable();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [holding_public?.vendable]);

  const [initialValues, setInitialValues] = useState<FormValuesShape>({
    serial: '',
    purchase_location: null,
    purchase_time: 0,
    store_not_listed: holding_private?.purchase_location_other ? true : false,
    custom_store_name: holding_private?.purchase_location_other ?? '',
    purchase_country: holding_private?.purchase_country ?? '',
  });

  useEffect(() => {
    void (async () => {
      const holding = await getSingleHolding(holdingId);
      if (holding instanceof Error) console.log('holding', holding);
      else {
        let additionalData = null;
        if (
          holding.additionalData &&
          config.HOLDINGS.NEW_REGISTRATION_FLOW.ADDITIONAL_FIELDS
        ) {
          additionalData = getEditableAdditionalDataFields(holding);
        }
        setInitialValues({
          serial: holding.serial || '',
          purchase_location: null,
          purchase_time: holding?.purchase_time ?? 0,
          store_not_listed: holding?.purchase_location_other ? true : false,
          custom_store_name: holding?.purchase_location_other ?? '',
          purchase_country: holding?.purchase_country ?? '',
          ...(additionalData ? additionalData : {}),
        });
        holding?.full_purchase_location &&
          setStoreLabel(
            `${holding?.full_purchase_location?.name} (${holding?.full_purchase_location?.address?.town} - ${holding?.full_purchase_location?.address?.county})`
          );
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (isReady && body) {
      setReadyToEdit(true);
      // setInitialValues({
      //   serial: body.public?.serial || '',
      //   // purchase_location: body.private?.purchase_location || '',
      //   purchase_time: fromUnixTime(date as number) || 0,
      // });
    }
  }, [isReady, body]);

  // Get the additional data from the holding to display in the form
  const getEditableAdditionalDataFields = (
    holding: ConsumerHoldingSummaryShape
  ) => {
    if (
      holding?.additionalData &&
      config.HOLDINGS.NEW_REGISTRATION_FLOW.ADDITIONAL_FIELDS
    ) {
      const editableAdditionalData = Object.entries(
        config.HOLDINGS.NEW_REGISTRATION_FLOW.ADDITIONAL_FIELDS
      ).filter(([_, field]) =>
        field.editable?.includes(EditableTypes.EDITABLE_FOR_CONSUMER)
      );
      const editableAdditionalDataWithKeyValues = editableAdditionalData.map(
        ([key, _]) => ({
          key,
          value: holding?.additionalData?.[key] ?? '',
        })
      );
      const editableObjectKeyValue = editableAdditionalDataWithKeyValues.reduce<
        Record<string, string | number | boolean | null>
      >((acc, curr) => {
        acc[curr.key] = curr.value;
        return acc;
      }, {});

      return editableObjectKeyValue;
    }

    return {};
  };

  const handleUpdate = async (values: FormValuesShape) => {
    // Since the body of the holding already exists, we can trust data to not be undefined

    // If values include a retailer from the database, then purchase_location_other should be cleared - and vice versa
    const newPurchaseLocation =
      values.custom_store_name && values.store_not_listed
        ? ''
        : values.purchase_location?.docid ??
        holding_private?.purchase_location ??
        '';

    const newPurchaseCountry =
      values.custom_store_name && values.store_not_listed
        ? values.purchase_country
        : values.purchase_location?.country ??
        holding_private?.purchase_country ??
        '';

    const newPurchaseLocationOther = values.store_not_listed
      ? values.custom_store_name ??
      holding_private?.purchase_location_other ??
      ''
      : null;

    // Get the additional data from the form to save in the holding
    const additionalData: Record<string, string | number | boolean | null> = {};
    if (config.HOLDINGS.NEW_REGISTRATION_FLOW.ADDITIONAL_FIELDS) {
      const editableFields = getEditableFields(
        config.HOLDINGS.NEW_REGISTRATION_FLOW.ADDITIONAL_FIELDS,
        EditableTypes.EDITABLE_FOR_CONSUMER
      );
      Object.entries(editableFields).forEach(([key, field]) => {
        additionalData[key] =
          (values as any)[key] ?? holding_private?.additionalData?.[key] ?? '';
      });
    }

    const {
      public: prevPublic,
      private: prevPrivate,
      manufacturer: prevManufacturer,
    } = body;

    // Verify serial number if validation is enabled
    const serialValidationEnabledNow = Boolean(
      config.HOLDINGS.SERIAL_NUMBER_VALIDATION.ENABLED
    );
    let serialVerified = false;
    let updateSerialNumber = '';
    if (serialValidationEnabledNow) {
      const serialToCheck = (values.serial || '').trim();
      if (serialToCheck) {
        serialVerified = isSerialNumberValid(
          serialToCheck,
          serialNumbers,
          serialNumberRanges
        );
      }
      updateSerialNumber = serialToCheck ?? prevPublic?.serial;
    }

    try {
      update({
        public: {
          ...prevPublic!,
          serial: updateSerialNumber,
          // todo make function to get purchase time, this is messy - dc
          purchase_time: values.purchase_time
            ? Math.floor(values.purchase_time)
            : prevManufacturer?.purchase_time
              ? prevManufacturer?.purchase_time
              : 0,
          ...(additionalData
            ? {
              additionalData: {
                ...prevPublic?.additionalData,
                ...additionalData,
              },
            }
            : {}),
          ...(serialValidationEnabledNow && {
            serialNumberVerified: serialVerified,
          }),
        },
        private: {
          ...prevPrivate!,
          purchase_location: newPurchaseLocation,
          purchase_location_other: newPurchaseLocationOther,
          purchase_country: newPurchaseCountry,
          ...(additionalData
            ? {
              additionalData: {
                ...prevPublic?.additionalData,
                ...additionalData,
              },
            }
            : {}),
          ...(serialValidationEnabledNow && {
            serialNumberVerified: serialVerified,
          }),
          serial: updateSerialNumber,
        },
        manufacturer: {
          ...prevManufacturer!,
          purchase_location: newPurchaseLocation,
          purchase_location_other: newPurchaseLocationOther,
          purchase_country: newPurchaseCountry,
          purchase_time: values.purchase_time
            ? Math.floor(values.purchase_time)
            : prevManufacturer?.purchase_time,
          ...(additionalData
            ? {
              additionalData: {
                ...prevPublic?.additionalData,
                ...additionalData,
              },
            }
            : {}),
          ...(serialValidationEnabledNow && {
            serialNumberVerified: serialVerified,
          }),
          serial: updateSerialNumber,
        },
      });
      await commit();
      setIsSubmitting(false);
      navigate(-1);
    } catch (err) {
      console.log(err);
      setIsSubmitting(false);
      throw new Error('Something went wrong while updating the holding');
    }
  };

  //TODO: CHECK SECOND UPDATE OF THE FILE, THROWING AN ERROR ON BROWSER CONSOLE
  const onSubmit = async (values: FormValuesShape) => {
    if (!readyToEdit) throw new Error('Not ready to edit');
    setIsSubmitting(true);
    void (await handleUpdate(values));
  };

  const getMakesModels = async () => {
    try {
      const data: any = await nestApiGetMakesModels({ tenantLink: t('tenantLink') });
      let tempFields = { ...fields };
      if (data.makes) {
        tempFields['make'].suggestion =
          data.makes?.map((s: any) => s.label) || [];
      }
      if (data.models) {
        tempFields['model'].suggestion =
          data.models?.map((s: any) => s.label) || [];
      }
      setFields(tempFields);
    } catch (error) {
      console.error('Error fetching makes/models:', error);
    }
  };

  useEffect(() => {
    if (config.MAKE_TAB.ENABLED && config.MODEL_TAB.ENABLED) {
      getMakesModels();
    }
  }, [config.MAKE_TAB.ENABLED, config.MODEL_TAB.ENABLED]);

  if (!holding_public || !holding_private || !holding_manufacturer)
    return <LoadingSpinner size="sm" isFullScreen />;

  if (holding_public.self_registration?.title) {
    const url = location.pathname.substring(
      0,
      location.pathname.lastIndexOf('/')
    );
    return <Navigate to={url + '/edit-info'} />;
  }

  return (
    <DesktopContainer
      title={t('Edit information')}
      goBack={() => navigate(-1)}
      loading={!readyToEdit || isSubmitting}
    >
      <div className="flex flex-col">
        {isLoading ? (
          <LoadingSpinner size="xs" />
        ) : (
          <Formik
            initialValues={initialValues}
            enableReinitialize={true}
            onSubmit={onSubmit}
          >
            {({ values, errors }) => {
              return (
                <Form className="font-nunito flex flex-col gap-5">
                  {!config.HOLDINGS.UPDATE_REGISTRATION_FLOW
                    .HIDE_PURCHASE_DATE && (
                      <div>
                        <Input
                          label={t('Purchase date')}
                          type="datepicker"
                          name="purchase_time"
                          settings={{
                            placeholder: t('Purchase date'),
                            maxDate: new Date(),
                            initialDate:
                              typeof values?.purchase_time == 'number'
                                ? new Date(values?.purchase_time)
                                : values?.purchase_time || new Date(),
                          }}
                        />
                      </div>
                    )}
                  {config.CLAIMS.CLAIMS_FLOW.SERIAL_NUMBER ? (
                    <div>
                      <Input
                        type="text"
                        label={t('Serial number')}
                        name="serial"
                        settings={{
                          disabled: !canEditSerial,
                          id: 'serial',
                          placeholder: t("The product's serial number"),
                          allowSpecialCharacter: false,
                        }}
                      />
                    </div>
                  ) : null}
                  {!config.HOLDINGS.UPDATE_REGISTRATION_FLOW.HIDE_STORE && (
                    <>
                      <div>
                        <Input
                          type="autocomplete-location-retailer"
                          name="purchase_location"
                          label={t('Store')}
                          settings={{
                            id: 'purchase_location',
                            placeholder: storeLabel,
                            isMulti: false,
                            disabled: values.store_not_listed,
                            options: [],
                          }}
                        />
                      </div>
                      <div>
                        <Input
                          type="checkbox"
                          name="store_not_listed"
                          settings={{
                            checkboxLabel: t('Store is not listed'),
                            checkboxLabelStyles: 'text-base text-gray-500',
                          }}
                        />
                      </div>
                      {values.store_not_listed && (
                        <div className="flex flex-col gap-4">
                          <Input
                            type="text"
                            name="custom_store_name"
                            settings={{
                              id: 'custom_store_name',
                              placeholder: t('Enter store name'),
                            }}
                          />
                          <Input
                            type="select"
                            label=""
                            name="purchase_country"
                            settings={{
                              options: LIST_COUNTRIES,
                              id: 'purchase_country',
                              placeholder: t(
                                'The country where the product was purchased'
                              ),
                            }}
                          />
                        </div>
                      )}
                    </>
                  )}
                  {fields && (
                    <CommonFormBuilder
                      fields={getEditableFields(
                        fields,
                        EditableTypes.EDITABLE_FOR_CONSUMER
                      )}
                      userType="olive"
                    />
                  )}
                  <Button
                    kind={'outline'}
                    size={'md'}
                    onClick={() =>
                      navigate(ROUTE_NAME.PRODUCTS + `/${holdingId}/documents`)
                    }
                  >
                    {t('View product documents')} ({productDocumentCount || '0'}
                    )
                  </Button>
                  <div className="mt-6 flex">
                    <Button
                      kind="primary"
                      type="submit"
                      loading={!readyToEdit || isSubmitting}
                      disabled={!readyToEdit && !!errors}
                    >
                      {t('Save')}
                    </Button>
                  </div>
                </Form>
              );
            }}
          </Formik>
        )}
      </div>
    </DesktopContainer>
  );
}

export default HoldingSingleEditView;
